export class ProductInfo extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['product-form']
  }

  static get optionalElements () {
    return [
      'bundle-slip-title',
      'bundle-slip-colour',
      'bundle-slip-variant-id',
      'bundle-slip-swatch',
      'bundle-slip-id',
      'bundle-slip-product-id',
      'bundle-slip-parent-id',
    ]
  }

  constructor () {
    super()

    this.variantChangeUnsubscriber = undefined
    this.variantDataCache = null
  }

  connectedCallback () {
    super.connectedCallback()

    // Load variant data from JSON script tag
    this.loadVariantData()

    // Subscribe to variant change events
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.onVariantChange.bind(this),
    )

    // Initialize Bloomreach BIS (Back in Stock) form
    this.initializeBloomreachBis()

    // Initialize bundle slip swatch click handlers
    this.initializeBundleSlipSwatches()
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber()
    }
  }

  /**
   * Loads variant data from JSON script tag
   */
  loadVariantData () {
    const sectionId = this.dataset.originalSection || this.dataset.section
    const variantDataEl = document.getElementById(`ProductVariantData-${sectionId}`)

    if (variantDataEl) {
      try {
        this.variantDataCache = JSON.parse(variantDataEl.textContent)
      } catch (error) {
        console.error('[ProductInfo] Error parsing variant data:', error)
        this.variantDataCache = {}
      }
    }
  }

  /**
   * Gets variant data from cache
   * @param {number} variantId - The variant ID
   * @returns {Object|null} The variant data or null
   */
  getVariantData (variantId) {
    if (!this.variantDataCache) return null
    return this.variantDataCache[variantId] || null
  }

  /**
   * Handles variant change events
   * Updates all variant-related UI using cached variant data
   * @param {Object} event - The variant change event data
   */
  async onVariantChange (event) {
    if (!event?.data?.sectionId || !event?.data?.variant) {
      console.warn('[ProductInfo] Missing event data, skipping')
      return
    }

    const sectionId = this.dataset.originalSection || this.dataset.section
    const sectionMatch = event.data.sectionId === sectionId

    if (!sectionMatch) return

    const { variant } = event.data

    // Get extended variant data from cache
    const variantData = this.getVariantData(variant.id)

    // Update things that use variant object directly
    this.updateMedia(variant)
    this.updateURL(variant)
    this.updateShareUrl(variant)
    this.updatePickupAvailability(variant)

    // Update things that can use cached data
    if (variantData) {
      this.updatePriceFromData(variantData, sectionId)
      this.updateSkuFromData(variantData, sectionId)
      this.updateButtonStateFromData(variantData, sectionId)
      this.updateInventoryFromData(variantData, sectionId)
      this.updateQuantityRulesFromData(variantData, sectionId)

      // Update slip swatches from cached data instead of fetching!
      this.updateSlipSwatchesFromData(variantData, variant)
    }

    // Trigger Bloomreach BIS event if variant is out of stock
    if (variant && !variant.available) {
      this.bloomreachBisEvent()
    }

    // Only fetch HTML if we actually need dynamic content that can't be pre-rendered
    // (like volume pricing or price per item which might need complex rendering)
    const needsVolumePricing = document.getElementById(`Volume-${sectionId}`)
    const needsPricePerItem = document.getElementById(`Price-Per-Item-${sectionId}`)
    const needsProductAddOn = document.querySelector('.product-add-on')

    if (needsVolumePricing || needsPricePerItem || needsProductAddOn) {
      await this.fetchAndUpdateDynamicContent(variant.id, event.data.productUrl || this.dataset.url, sectionId)
    }
  }

  /**
   * Fetches HTML only for dynamic content that can't be pre-rendered in JSON
   * (volume pricing, price per item, product add-ons)
   * @param {number} variantId - The variant ID
   * @param {string} productUrl - The product URL
   * @param {string} sectionId - The section ID
   */
  async fetchAndUpdateDynamicContent (variantId, productUrl, sectionId) {
    try {
      const fetchUrl = `${productUrl}?variant=${variantId}&section_id=${sectionId}`

      const response = await fetch(fetchUrl)
      const responseText = await response.text()

      const html = new DOMParser().parseFromString(responseText, 'text/html')

      // Note: The following are now updated from cached JSON data:
      // - Slip swatches (updateSlipSwatchesFromData)
      // - Inventory status (updateInventoryFromData)
      // - Quantity rules (updateQuantityRulesFromData)
      // No need to fetch them from HTML anymore!

      // Update volume pricing (might have variant-specific content)
      this.updateVolumePricing(html, sectionId)

      // Update price per item
      this.updatePricePerItem(html, sectionId)

      // Update product add-on
      this.updateProductAddOn(html)
    } catch (error) {
      console.error('Error fetching dynamic content:', error)
    }
  }

  /**
   * Updates price display from cached variant data
   * @param {Object} variantData - The cached variant data
   * @param {string} sectionId - The section ID
   */
  updatePriceFromData (variantData, sectionId) {
    const priceDestination = document.getElementById(`price-${sectionId}`)
    if (!priceDestination) return

    // Use the formatted price strings from variant data (respects market currency)
    const priceFormatted = variantData.price_formatted
    const compareAtPriceFormatted = variantData.compare_at_price_formatted

    // Show/hide compare at price
    if (compareAtPriceFormatted && variantData.compare_at_price > variantData.price) {
      const compareElement = priceDestination.querySelector('.price-item--sale')
      if (compareElement) {
        compareElement.innerHTML = priceFormatted
        compareElement.classList.remove('hidden')
      }

      // Update price display - use innerHTML to render the HTML from money_with_currency filter
      const priceElement = priceDestination.querySelector('.price-item--regular')
      if (priceElement && priceFormatted) {
        priceElement.innerHTML = compareAtPriceFormatted
      }
    } else {
      // Update price display - use innerHTML to render the HTML from money_with_currency filter
      const priceElement = priceDestination.querySelector('.price-item--regular')
      if (priceElement && priceFormatted) {
        priceElement.innerHTML = priceFormatted
      }
    }

    priceDestination.classList.remove('hidden')
  }

  /**
   * Updates SKU display from cached variant data
   * @param {Object} variantData - The cached variant data
   * @param {string} sectionId - The section ID
   */
  updateSkuFromData (variantData, sectionId) {
    const skuDestination = document.getElementById(`Sku-${sectionId}`)
    if (!skuDestination) return

    if (variantData.sku) {
      skuDestination.textContent = variantData.sku
      skuDestination.classList.remove('visibility-hidden', 'hidden')
    } else {
      skuDestination.classList.add('visibility-hidden')
    }
  }

  /**
   * Updates button state from cached variant data
   * @param {Object} variantData - The cached variant data
   * @param {string} sectionId - The section ID
   */
  updateButtonStateFromData (variantData, sectionId) {
    const productForm = this.els['product-form']?.element
    if (!productForm) return

    if (!variantData.available) {
      productForm.toggleAddButton(true, window.variantStrings.soldOut)
    } else {
      productForm.toggleAddButton(false)
    }
  }

  /**
   * Updates inventory status from cached variant data
   * @param {Object} variantData - The cached variant data
   * @param {string} sectionId - The section ID
   */
  updateInventoryFromData (variantData, sectionId) {
    const inventoryElement = document.getElementById(`Inventory-${sectionId}`)
    if (!inventoryElement) return

    const inventoryMessage = variantData.html?.inventory || ''
    inventoryElement.textContent = inventoryMessage
    inventoryElement.classList.toggle('hidden', inventoryMessage === '')
  }

  /**
   * Updates quantity rules from cached variant data
   * @param {Object} variantData - The cached variant data
   * @param {string} sectionId - The section ID
   */
  updateQuantityRulesFromData (variantData, sectionId) {
    const productForm = this.els['product-form']?.element
    if (!productForm) return

    const quantityInput = productForm.querySelector('.quantity__input')
    if (!quantityInput) return

    const rules = variantData.html?.quantity_rules
    if (!rules) return

    // Update quantity input attributes
    quantityInput.setAttribute('data-min', rules.min)
    if (rules.max) {
      quantityInput.setAttribute('data-max', rules.max)
    } else {
      quantityInput.removeAttribute('data-max')
    }
    quantityInput.setAttribute('step', rules.increment)

    // Update the actual input value if it's below the minimum
    const currentValue = parseInt(quantityInput.value) || 1
    if (currentValue < rules.min) {
      quantityInput.value = rules.min
    }

    // Call setQuantityBoundries on the product form to recalculate boundaries
    if (typeof productForm.setQuantityBoundries === 'function') {
      productForm.setQuantityBoundries()
    }
  }

  /**
   * Updates the product form button state based on availability
   * @param {Document} html - The parsed HTML document from the variant change
   * @param {string} sectionId - The section ID
   */
  updateProductFormButton (html, sectionId) {
    const productForm = this.els['product-form']?.element

    if (!productForm) return

    const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`)

    if (addButtonUpdated && addButtonUpdated.hasAttribute('disabled')) {
      productForm.toggleAddButton(true, window.variantStrings.soldOut)
    } else if (addButtonUpdated) {
      productForm.toggleAddButton(false)
    } else {
      productForm.setUnavailable()
    }
  }

  /**
   * Updates the slip swatches when variant changes
   * @param {Document} html - The parsed HTML document from the variant change
   */
  updateSlipSwatches (html) {
    const currentSlipContainer = this.querySelector('.prd-Slip')
    if (!currentSlipContainer) {
      return
    }

    const newSlipContainer = html.querySelector('.prd-Slip')
    if (!newSlipContainer) {
      // Don't remove - keep the previous slip options visible
      // This is a fallback for when the metafield is not set
      return
    }

    // Replace the entire slip container with the new one
    currentSlipContainer.innerHTML = newSlipContainer.innerHTML

    // Re-initialize click handlers for the new swatches
    this.initializeBundleSlipSwatches()
  }

  /**
   * Updates slip swatches from cached variant data (no fetch needed!)
   * @param {Object} variantData - The cached variant data
   * @param {Object} variant - The current variant
   */
  updateSlipSwatchesFromData (variantData, variant) {
    const productForm = this.els['product-form']?.element

    // Reset hidden inputs
    if (this.els['bundle-slip-variant-id']?.exists) {
      this.els['bundle-slip-variant-id'].element.value = ''
    }
    if (this.els['bundle-slip-colour']?.exists) {
      this.els['bundle-slip-colour'].element.value = ''
    }
    if (this.els['bundle-slip-title']?.exists) {
      this.els['bundle-slip-title'].element.textContent = ''
    }
    if (this.els['bundle-slip-id']?.exists) {
      this.els['bundle-slip-id'].element.value = ''
    }
    if (this.els['bundle-slip-product-id']?.exists) {
      this.els['bundle-slip-product-id'].element.value = ''
    }
    if (this.els['bundle-slip-parent-id']?.exists) {
      this.els['bundle-slip-parent-id'].element.value = variant.id
    }

    const slipContainer = this.querySelector('.prd-Slip')
    if (!slipContainer) {
      return
    }

    // Check if variant has bundle data (now it's an array of slip variants)
    const bundleVariants = variantData?.metafields?.bundle?.partner_variant

    if (!bundleVariants || !Array.isArray(bundleVariants) || bundleVariants.length === 0) {
      slipContainer.style.display = 'none'
      return
    }

    slipContainer.style.display = ''

    // Get the current size
    const currentSize = variant.title || variant.option1

    // Update active state on all swatches
    const allSwatches = this.querySelectorAll('[data-product-info-el="bundle-slip-swatch"]')

    let firstAvailableSwatch = null
    let hasAnyAvailableSlip = false

    allSwatches.forEach((swatch) => {
      const swatchSize = swatch.dataset.size
      const isForCurrentSize = swatchSize === currentSize
      const isAvailable = swatch.dataset.available === 'true'

      if (isForCurrentSize) {
        swatch.classList.add('prd-Slip_Swatch-active')

        if (isAvailable) {
          hasAnyAvailableSlip = true

          // Track first available swatch for auto-selection
          if (!firstAvailableSwatch) {
            firstAvailableSwatch = swatch
          }
        }
      } else {
        swatch.classList.remove('prd-Slip_Swatch-active', 'prd-Slip_Swatch-selected')
      }
    })

    // Auto-select first available swatch if none selected
    const selectedSwatch = this.querySelector('.prd-Slip_Swatch-selected.prd-Slip_Swatch-active')

    if (!selectedSwatch && firstAvailableSwatch) {
      // Auto-select first available
      firstAvailableSwatch.classList.add('prd-Slip_Swatch-selected')

      // Update all hidden inputs
      if (this.els['bundle-slip-variant-id']?.exists) {
        this.els['bundle-slip-variant-id'].element.value = firstAvailableSwatch.dataset.variantId
      }
      if (this.els['bundle-slip-colour']?.exists) {
        this.els['bundle-slip-colour'].element.value = firstAvailableSwatch.dataset.colour
      }
      if (this.els['bundle-slip-title']?.exists) {
        this.els['bundle-slip-title'].element.textContent = firstAvailableSwatch.dataset.colour
      }
      if (this.els['bundle-slip-id']?.exists) {
        this.els['bundle-slip-id'].element.value = firstAvailableSwatch.dataset.variantId
      }
      if (this.els['bundle-slip-product-id']?.exists) {
        this.els['bundle-slip-product-id'].element.value = firstAvailableSwatch.dataset.productId
      }

      // Enable button if dress variant is available and we found an available slip
      if (variantData.available && hasAnyAvailableSlip) {
        productForm.toggleAddButton(false)
      } else {
        productForm.toggleAddButton(true, window.variantStrings.soldOut)
      }
    } else if (selectedSwatch) {
      // Check if the currently selected swatch is still available
      const isSelectedAvailable = selectedSwatch.dataset.available === 'true'

      if (!isSelectedAvailable) {
        // Currently selected slip is OOS, disable button
        productForm.toggleAddButton(true, window.variantStrings.soldOut)
      } else if (variantData.available) {
        // Both dress and slip are available, enable button
        productForm.toggleAddButton(false)
      } else {
        // Dress is OOS
        productForm.toggleAddButton(true, window.variantStrings.soldOut)
      }
    } else {
      // No slip available for this size
      productForm.toggleAddButton(true, window.variantStrings.soldOut)
    }
  }

  /**
   * Updates the price display
   * @param {Document} html - The parsed HTML document
   * @param {string} sectionId - The section ID
   */
  updatePriceDisplay (html, sectionId) {
    const priceDestination = document.getElementById(`price-${sectionId}`)
    const priceSource = html.getElementById(`price-${sectionId}`)

    if (priceSource && priceDestination) {
      priceDestination.innerHTML = priceSource.innerHTML
    }
  }

  /**
   * Updates the SKU display
   * @param {Document} html - The parsed HTML document
   * @param {string} sectionId - The section ID
   */
  updateSku (html, sectionId) {
    const skuDestination = document.getElementById(`Sku-${sectionId}`)
    const skuSource = html.getElementById(`Sku-${sectionId}`)

    if (skuSource && skuDestination) {
      skuDestination.innerHTML = skuSource.innerHTML
      skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'))
    }
  }

  /**
   * Updates the inventory status display
   * @param {Document} html - The parsed HTML document
   * @param {string} sectionId - The section ID
   */
  updateInventoryStatus (html, sectionId) {
    const inventoryDestination = document.getElementById(`Inventory-${sectionId}`)
    const inventorySource = html.getElementById(`Inventory-${sectionId}`)

    if (inventorySource && inventoryDestination) {
      inventoryDestination.innerHTML = inventorySource.innerHTML
      inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '')
    }
  }

  /**
   * Updates volume pricing display
   * @param {Document} html - The parsed HTML document
   * @param {string} sectionId - The section ID
   */
  updateVolumePricing (html, sectionId) {
    const volumePricingDestination = document.getElementById(`Volume-${sectionId}`)
    const volumePricingSource = html.getElementById(`Volume-${sectionId}`)
    const volumeNote = document.getElementById(`Volume-Note-${sectionId}`)
    const qtyRules = document.getElementById(`Quantity-Rules-${sectionId}`)

    if (volumeNote) volumeNote.classList.remove('hidden')
    if (volumePricingDestination) volumePricingDestination.classList.remove('hidden')
    if (qtyRules) qtyRules.classList.remove('hidden')

    if (volumePricingSource && volumePricingDestination) {
      volumePricingDestination.innerHTML = volumePricingSource.innerHTML
    }
  }

  /**
   * Updates price per item display
   * @param {Document} html - The parsed HTML document
   * @param {string} sectionId - The section ID
   */
  updatePricePerItem (html, sectionId) {
    const pricePerItemDestination = document.getElementById(`Price-Per-Item-${sectionId}`)
    const pricePerItemSource = html.getElementById(`Price-Per-Item-${sectionId}`)

    if (pricePerItemSource && pricePerItemDestination) {
      pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML
      pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'))
    }
  }

  /**
   * Updates product add-on display
   * @param {Document} html - The parsed HTML document
   */
  updateProductAddOn (html) {
    const productAddOnSource = html.querySelector('.product-add-on')
    const productAddOnDestination = document.querySelector('.product-add-on')

    if (productAddOnSource && productAddOnDestination) {
      productAddOnDestination.innerHTML = productAddOnSource.innerHTML
    }
  }

  /**
   * Updates the media gallery to show variant's featured media
   * @param {Object} variant - The selected variant
   */
  updateMedia (variant) {
    if (!variant || !variant.featured_media) return

    const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`)
    mediaGalleries.forEach((mediaGallery) =>
      mediaGallery.setActiveMedia(`${this.dataset.section}-${variant.featured_media.id}`, true),
    )

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`)
    if (!modalContent) return

    const newMediaModal = modalContent.querySelector(`[data-media-id="${variant.featured_media.id}"]`)
    if (newMediaModal) {
      modalContent.prepend(newMediaModal)
    }
  }

  /**
   * Updates the browser URL with the variant parameter
   * @param {Object} variant - The selected variant
   */
  updateURL (variant) {
    if (!variant || this.dataset.updateUrl === 'false') return
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`)
  }

  /**
   * Updates the share button URL
   * @param {Object} variant - The selected variant
   */
  updateShareUrl (variant) {
    if (!variant) return

    const shareButton = document.getElementById(`Share-${this.dataset.section}`)
    if (!shareButton || !shareButton.updateUrl) return
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variant.id}`)
  }

  /**
   * Updates pickup availability
   * @param {Object} variant - The selected variant
   */
  updatePickupAvailability (variant) {
    const pickUpAvailability = document.querySelector('pickup-availability')
    if (!pickUpAvailability) return

    if (variant && variant.available) {
      pickUpAvailability.fetchAvailability(variant.id)
    } else {
      pickUpAvailability.removeAttribute('available')
      pickUpAvailability.innerHTML = ''
    }
  }

  // ========================================
  // Bundle Slip Swatches
  // ========================================

  /**
   * Initialize bundle slip swatch click handlers
   */
  initializeBundleSlipSwatches () {
    if (!this.els['bundle-slip-swatch']?.exists) {
      return
    }

    // Get all slip swatches
    const swatches = this.els['bundle-slip-swatch'].elements

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', this.onSlipSwatchClick.bind(this))
    })
  }

  /**
   * Handles slip swatch click events
   * @param {Event} event - The click event
   */
  onSlipSwatchClick (event) {
    const clickedSwatch = event.currentTarget

    // Only process active swatches that are available
    if (!clickedSwatch.classList.contains('prd-Slip_Swatch-active')) return
    if (clickedSwatch.dataset.available !== 'true') return

    const variantId = clickedSwatch.dataset.variantId
    const productId = clickedSwatch.dataset.productId
    const colour = clickedSwatch.dataset.colour

    // Update the hidden inputs
    if (this.els['bundle-slip-variant-id']?.exists) {
      this.els['bundle-slip-variant-id'].element.value = variantId
    }

    if (this.els['bundle-slip-colour']?.exists) {
      this.els['bundle-slip-colour'].element.value = colour
    }

    // Update the title display
    if (this.els['bundle-slip-title']?.exists) {
      this.els['bundle-slip-title'].element.textContent = colour
    }

    // Update the items[1][id] input for bundle submission
    if (this.els['bundle-slip-id']?.exists) {
      this.els['bundle-slip-id'].element.value = variantId
    }

    // Update the items[1][product_id] input for bundle submission
    if (this.els['bundle-slip-product-id']?.exists) {
      this.els['bundle-slip-product-id'].element.value = productId
    }

    // Update selected state visually
    const allActiveSwatches = this.querySelectorAll('.prd-Slip_Swatch-active')
    allActiveSwatches.forEach((swatch) => {
      swatch.classList.remove('prd-Slip_Swatch-selected')
    })
    clickedSwatch.classList.add('prd-Slip_Swatch-selected')

    // Enable the add to cart button since we just selected an available slip
    const productForm = this.els['product-form']?.element
    if (productForm) {
      // Get the current variant input to check if dress is available
      const variantInput = productForm.querySelector('input[name="id"]')
      const isDressAvailable = variantInput && !variantInput.hasAttribute('disabled')

      if (isDressAvailable) {
        productForm.toggleAddButton(false)
      }
    }
  }

  // ========================================
  // Bloomreach BIS (Back in Stock)
  // ========================================

  /**
   * Initialize Bloomreach Back in Stock form
   */
  initializeBloomreachBis () {
    setTimeout(() => {
      this.notifyMeForm = document.querySelector('.appikon-bis-inline-form-submit')
      this.notifyEmail = document.getElementById('appikon-bis-inline-form-email')
      this.notifyNumber = document.getElementById('appikon-bis-inline-form-phone-no')

      if (this.notifyMeForm) {
        const selectedFlag = document.querySelector('.iti__flag-container .iti__selected-flag')
        if (selectedFlag) {
          this.countryCode = selectedFlag.getAttribute('title').split(':')[1]
        }
        this.notifyMeForm.addEventListener('click', () => this.bloomreachBisEvent())
      }
    }, 2000)
  }

  /**
   * Triggers Bloomreach tracking for back in stock notifications
   */
  bloomreachBisEvent () {
    if (!window.exponea) return

    if (this.notifyNumber && this.notifyNumber.value !== '') {
      exponea.track('joined_waitlist', {
        phone_number: `${parseInt(this.countryCode)}${this.notifyNumber.value}`,
        product_title: this.dataset.title?.toString(),
        product_id: Number(this.dataset.id),
      })

      exponea.track('consent', {
        action: 'accept',
        category: 'SMS',
        valid_until: 'unlimited',
        message: 'This consent was tracked from Product Details page',
      })
    } else if (this.notifyEmail && this.notifyEmail.value !== '' && this.emailValidation(this.notifyEmail.value)) {
      exponea.track('joined_waitlist', {
        product_title: this.dataset.title?.toString(),
        product_id: this.dataset.id,
      })

      exponea.track('consent', {
        action: 'accept',
        category: 'Waitlist',
        valid_until: 'unlimited',
        message: 'This Legitimate Intrest was tracked from Product Details page',
      })

      exponea.identify(
        { email_id: this.notifyEmail.value.toLowerCase().trim() },
        { email: this.notifyEmail.value.toLowerCase().trim() },
      )
    }
  }

  /**
   * Validates email format
   * @param {string} value - The email to validate
   * @returns {boolean}
   */
  emailValidation (value) {
    const re = /\S+@\S+\.\S+/
    return re.test(value)
  }
}

customElements.define('product-info', ProductInfo)
